import React from 'react'

import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  IconButton,
  Text,
  Stack,
  ModalCloseButton,
} from '@chakra-ui/react'
import { Link } from 'react-router'
import { PaginationNext } from 'src/components/__legacy__/icon/monochrome'
import { Message } from 'src/components/__legacy__/message/message'
import { Add, Close } from 'src/components/icon'
import { Menu as MenuIcon } from 'src/components/icon'
import { useOpenPullDown } from 'src/hooks/use-open-pull-down'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'

import { IcfDocumentCardRenewal } from '../components/document-card-renewal'
import { CreateIcfDocumentItem, IcfDocument, IcfDocumentType } from '../entity'
import { IcfDocumentErrors } from '../validate'

type IcfDocumentErrorMap = Record<IcfDocument['uid'], IcfDocumentErrors>

type Props = {
  createIcfDocumentItems: CreateIcfDocumentItem[]
  onAddItem: () => void
  onDeleteItem: (uid: string) => void
  onChangeDocumentType: ({
    uid,
    type,
  }: {
    uid: string
    type: IcfDocumentType
  }) => void
  onChangeName: ({ uid, value }: { uid: string; value: string }) => void
  onChangeVersion: ({ uid, value }: { uid: string; value: string }) => void
  onSelectFile: ({ uid, file }: { uid: string; file: File }) => Promise<void>
  onResetFile: ({ uid }: { uid: string }) => void
  onChangeRequiredPatientSign: ({ uid }: { uid: string }) => void
  tempFileUrlMap: Map<CreateIcfDocumentItem['uid'], string>
  submitModalOpen: boolean
  openSubmitModal: () => void
  closeSubmitModal: () => void
  onValidate: () => boolean
  errorMap: IcfDocumentErrorMap
  shouldSelectHospital: boolean
  hospitalName: string
  listPath: string
  pullDownState: ReturnType<typeof useOpenPullDown>
  maxDocumentCount: number
  onCancel: () => void
  onSubmit: () => Promise<void>
}

export const AddIcfDocumentRenewal: React.FC<Props> = ({
  createIcfDocumentItems,
  onAddItem,
  onDeleteItem,
  onChangeDocumentType,
  onChangeName,
  onChangeVersion,
  onSelectFile,
  onResetFile,
  onChangeRequiredPatientSign,
  tempFileUrlMap,
  submitModalOpen,
  openSubmitModal,
  closeSubmitModal,
  onValidate,
  errorMap,
  shouldSelectHospital,
  hospitalName,
  listPath,
  onCancel,
  maxDocumentCount,
  onSubmit,
}) => {
  return (
    <Flex direction="column" h="full" py="6">
      {submitModalOpen && (
        <SubmitModal
          onClose={closeSubmitModal}
          onSubmit={onSubmit}
          itemCount={createIcfDocumentItems.length}
        />
      )}
      <Flex justify="space-between" px="6">
        <Breadcrumb
          display="flex"
          alignItems="center"
          separator={<PaginationNext size="M" />}
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={listPath}
              color="blue.500"
              fontSize="lg"
              fontWeight="bold"
            >
              文書一覧
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem fontSize="lg" isCurrentPage>
            <BreadcrumbLink
              fontWeight="bold"
              cursor="text"
              _hover={{ textDecoration: 'none' }}
            >
              文書登録
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <HStack spacing="4">
          {shouldSelectHospital && (
            <Text fontSize="sm" fontWeight="bold">
              医療機関 : {hospitalName}
            </Text>
          )}

          <Button
            onClick={() => {
              if (!onValidate()) {
                return
              }
              openSubmitModal()
            }}
            isDisabled={createIcfDocumentItems.length === 0}
          >
            登録
          </Button>

          {/* メニュー不要では？ */}
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="open menu"
              icon={<MenuIcon />}
              variant="customIconButtonGhost"
            />
            <MenuList>
              <MenuItem onClick={onCancel} color="red.500">
                キャンセル
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <Box overflow="auto" flex="1" mt="8" pt="4" px="6">
        <Stack spacing="4">
          {createIcfDocumentItems.map(item => (
            <HStack
              key={item.uid}
              spacing="4"
              w="full"
              minW="640px"
              maxW="780px"
            >
              <IcfDocumentCardRenewal
                cardType="new"
                documentType={item.type}
                name={item.revision.name}
                version={item.revision.version}
                fileName={item.revision.fileName}
                isRequiredPatientSign={item.revision.isRequiredPatientSign}
                onChangeName={(value: string) => {
                  onChangeName({ uid: item.uid, value })
                }}
                onChangeVersion={(value: string) => {
                  onChangeVersion({ uid: item.uid, value })
                }}
                onSelectFile={async (file: File) => {
                  await onSelectFile({ uid: item.uid, file })
                }}
                onResetFile={() => onResetFile({ uid: item.uid })}
                onChangeDocumentType={(type: IcfDocumentType) => {
                  onChangeDocumentType({ uid: item.uid, type })
                }}
                onChangeRequiredPatientSign={() =>
                  onChangeRequiredPatientSign({ uid: item.uid })
                }
                filePreviewUrl={tempFileUrlMap.get(item.uid)}
                hasTempFile={!!tempFileUrlMap.get(item.uid)}
                errors={errorMap[item.uid]}
              />
              <IconButton
                aria-label="delete"
                variant="customIconButtonGhost"
                icon={<Close />}
                onClick={() => onDeleteItem(item.uid)}
              />
            </HStack>
          ))}
        </Stack>
        <Box mt="6">
          {createIcfDocumentItems.length === maxDocumentCount ? (
            <Message
              type="information"
              message={`一度に追加できる文書は${maxDocumentCount}件までです`}
            />
          ) : (
            <Button variant="outline" leftIcon={<Add />} onClick={onAddItem}>
              文書を追加
            </Button>
          )}
        </Box>
      </Box>
    </Flex>
  )
}

type ModalProps = {
  onClose: () => void
  onSubmit: () => Promise<void>
  itemCount: number
}
const SubmitModal: React.FC<ModalProps> = ({
  onClose,
  onSubmit,
  itemCount,
}) => {
  return (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>文書登録</ModalHeader>
        <ModalBody>{itemCount}件の文書を新規登録します。</ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button onClick={onSubmit}>登録する</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
